// src/components/dashboards/DisambiguationPanel.tsx
// Disambiguation UI (feature 3). When the NL pipeline cannot confidently pick a single reading of
// the question, the backend returns 2-4 interpretation OPTIONS, each with a plain-language label +
// explanation from the judge. This component ONLY designs the OUTER framing: the "this can be read a
// few ways" intro, the expandable section headers (the Patient-Details GlassBox/ExpandToggle
// pattern), and the select/refine controls. The body of each section reuses the EXISTING result
// presentation via <NLResultCard> — same colors, same table header, same chart, same clickable
// patient links — so nothing about the working result UI is reinvented. Joan, 2026-06-23.

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GlassBox from "../layout/GlassBox";
import ExpandToggle from "../layout/ExpandToggle";
import NLResultCard from "./NLResultCard";
import { useDashboardInk } from "./dashboardInk";
import { glassContentSx } from "../../theme/glassTokens";
import { NLQueryResult, NLQueryOption } from "hooks/useNLQuery";
import { useTranslation } from "../../i18n";

// Only these completeness values get a chip; guards against the judge ever returning free text.
const COMPLETENESS_VALUES = ["high", "medium", "low"];

interface DisambiguationPanelProps {
  result: NLQueryResult;
  /** Re-submit the original question with a 'select' directive for this interpretation. */
  onSelect: (interpretationId: string) => void;
  /** Re-submit the original question with a 'refine' directive carrying the user's clarification. */
  onRefine: (instruction: string) => void;
  /** Full-cohort export for an option's patient list (threaded through to NLResultCard). */
  onExportSelected?: (ids: number[]) => void;
  hideInputBar?: boolean;
}

const DisambiguationPanel: React.FC<DisambiguationPanelProps> = ({
  result,
  onSelect,
  onRefine,
  onExportSelected,
  hideInputBar,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const options = result.options || [];

  const suggested = options.find((o) => o.suggested) || options[0];
  const ink = useDashboardInk();
  const [expandedId, setExpandedId] = useState<string | undefined>(suggested?.interpretation_id);
  const [refineText, setRefineText] = useState("");

  if (options.length === 0) return null;

  const completenessColor = (c: string): string => {
    if (c === "high") return theme.palette.success.main;
    if (c === "medium") return theme.palette.warning.main;
    if (c === "low") return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const submitRefine = () => {
    const instr = refineText.trim();
    if (!instr) return;
    onRefine(instr);
    setRefineText("");
  };

  const renderOptionBody = (opt: NLQueryOption) => {
    const rowCount = opt.row_count ?? opt.rows?.length ?? 0;
    return (
      <Box>
        {/* The interpretation's plain-language explanation (what this reading considered). */}
        {opt.interpretation_explanation && (
          <Typography
            variant="body2"
            sx={{
              px: hideInputBar ? { xs: 2.5, sm: 5 } : 2,
              pt: 1.5,
              color: ink.dark
                ? alpha(theme.palette.common.white, 0.7)
                : theme.palette.text.secondary,
            }}
          >
            {opt.interpretation_explanation}
          </Typography>
        )}

        {/* The EXISTING result presentation, reused verbatim (chart + patient links + table). */}
        <NLResultCard
          result={opt}
          hideInputBar={hideInputBar}
          onExportSelected={onExportSelected}
          showQuestionPill={false}
          showExplanation={false}
          showCachedStrip={false}
          showFollowups={false}
        />

        {/* The select / CSV controls — the outer affordance this panel adds. */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
            px: hideInputBar ? { xs: 2.5, sm: 5 } : 2,
            py: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => onSelect(opt.interpretation_id)}
          >
            {t("dashboard_chat.disambiguation.use_this", "Use this interpretation")}
          </Button>
          {/* No per-interpretation CSV download here (066 red-team): an ambiguous option's
              CSV cannot be reliably re-run export-scoped from the session cache. To download,
              "Use this interpretation" to commit it as a normal result, then download from
              there (that path is export-scope-gated end-to-end). */}
          <Typography
          variant="caption"
          sx={{
            color: ink.dark
              ? alpha(theme.palette.common.white, 0.7)
              : theme.palette.text.secondary,
            ml: "auto",
          }}
        >
            {t("dashboard_chat.disambiguation.rows", "{count} rows").replace("{count}", String(rowCount))}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    // Ink at the ROOT, not per Typography. The panel's own copy (the judge
    // rationale, the row-count caption) is theme.palette.text.secondary, which
    // is royalBlue: legible on a white card, a dim blue smudge here. Wrapping
    // is what NLResultCard does and it is the only version that stays correct
    // when new copy is added.
    <Box sx={{ mt: 1.5, ...(ink.dark ? glassContentSx : {}) }}>
      {/* The question asked — shown once, clearly, at the top (mirrors the normal result pill). */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          mb: 1.5,
          maxWidth: "100%",
          borderRadius: "12px",
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.95)} 0%, ${alpha(
            theme.palette.primary.dark,
            0.85,
          )} 100%)`,
          color: theme.palette.common.white,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 15, color: theme.palette.secondary.main, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem", lineHeight: 1.3 }}>
          {result.question}
        </Typography>
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {t("dashboard_chat.disambiguation.title", "This question can be read a few ways")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: ink.dark
            ? alpha(theme.palette.common.white, 0.75)
            : theme.palette.text.secondary,
          mb: 1.5,
        }}
      >
        {t(
          "dashboard_chat.disambiguation.subtitle",
          "Pick the reading you meant, or refine your question below.",
        )}
        {result.judge_rationale ? ` ${result.judge_rationale}` : ""}
      </Typography>

      {options.map((opt) => {
        const isOpen = expandedId === opt.interpretation_id;
        const showCompleteness = COMPLETENESS_VALUES.includes(opt.completeness);
        const cColor = showCompleteness ? completenessColor(opt.completeness) : theme.palette.text.secondary;
        return (
          <Box key={opt.interpretation_id} sx={{ mb: 1 }}>
            <GlassBox
              variant="filled"
              expanded={isOpen}
              onClick={() => setExpandedId(isOpen ? undefined : opt.interpretation_id)}
              sx={{ px: { xs: 2, sm: 3 }, py: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, color: theme.palette.common.white }}>
                  {opt.label}
                </Typography>
                {opt.suggested && (
                  <Chip
                    size="small"
                    label={t("dashboard_chat.disambiguation.suggested", "Suggested")}
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.secondary.main, 0.9),
                      color: theme.palette.common.white,
                    }}
                  />
                )}
                {showCompleteness && (
                  <Chip
                    size="small"
                    label={t(
                      `dashboard_chat.disambiguation.completeness_${opt.completeness}`,
                      opt.completeness,
                    )}
                    sx={{
                      height: 20,
                      fontSize: "0.62rem",
                      bgcolor: alpha(cColor, 0.18),
                      color: theme.palette.common.white,
                      border: `1px solid ${alpha(cColor, 0.6)}`,
                    }}
                  />
                )}
              </Box>
              <ExpandToggle expanded={isOpen} variant="onDark" />
            </GlassBox>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              {renderOptionBody(opt)}
            </Collapse>
          </Box>
        );
      })}

      {/* Refine: none of the readings fit — let the user clarify and re-run guided. */}
      <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "center" }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t(
            "dashboard_chat.disambiguation.refine_placeholder",
            "None of these? Add a clarification...",
          )}
          value={refineText}
          onChange={(e) => setRefineText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitRefine();
            }
          }}
        />
        <Button variant="outlined" size="small" disabled={!refineText.trim()} onClick={submitRefine}>
          {t("dashboard_chat.disambiguation.refine", "Refine")}
        </Button>
      </Box>
    </Box>
  );
};

export default DisambiguationPanel;
